/**
 * What account managers are told about the products customers compare iBigDo with (Account Management > Compare).
 * Facts from vendor pages and reseller sites; they change, so update this file (and `COMPETITORS_CHECKED`) when an
 * account manager's market survey finds something different. iBigDo's own prices are not here: the page reads them
 * from the live plan list.
 *
 * Prices are rupees, before 18% GST. A product is sold either as `oneTime` plus `renewal` a year (updates and support,
 * counted from the second year), or as `yearly`.
 */
export const COMPETITORS_CHECKED = '2026-10-17';

export const COMPETITOR_PRICES = [
  {
    id: 'tally-silver', name: 'Tally Prime Silver', users: '1 user, one PC', oneTime: 22500, renewal: 4500,
    note: 'Renewal (TSS) is optional but gives updates and support.',
  },
  {
    id: 'tally-gold', name: 'Tally Prime Gold', users: 'Many users on one network', oneTime: 67500, renewal: 13500,
    note: 'Remote access or hosting is extra.',
  },
  {
    id: 'busy-single', name: 'Busy Enterprise, single user', users: '1 user', yearly: 10000,
    note: "Busy's own page; its one-time price table differs.",
  },
  {
    id: 'busy-multi', name: 'Busy Enterprise, multi user', users: 'Many users', yearly: 25000,
    note: "Busy's own page; its one-time price table differs.",
  },
  {
    id: 'zoho-books', name: 'Zoho Books Professional', users: 'Extra users ₹150 a month each', yearly: 17988,
    note: '₹1,499 a month on yearly billing. Accounts and basic stock; Zoho Inventory is sold separately.',
  },
  {
    id: 'zoho-inventory', name: 'Zoho Inventory Professional', users: 'Per organization', yearly: 26999,
    note: 'Batch tracking starts at this plan. Add Zoho Books if you also need accounts.',
  },
  {
    id: 'marg-silver', name: 'Marg Silver', users: 'Per edition; dealers vary', oneTime: 12600, renewal: 4200,
    note: 'Dealers set their own prices. A cloud edition is about ₹21,400 a year.',
  },
  {
    id: 'marg-gold', name: 'Marg Gold', users: 'Per edition; dealers vary', oneTime: 25200, renewal: 7200,
    note: 'Dealers set their own prices.',
  },
  {
    id: 'vyapar', name: 'Vyapar Silver, desktop and mobile', users: 'Small teams', yearly: 4399,
    note: 'Resellers list ₹3,999 to ₹4,399 a year; no clear official price found.',
  },
];

/** What a customer pays over `years`, rupees. */
export function costOver(product, years) {
  if (product.yearly != null) return product.yearly * years;
  return product.oneTime + product.renewal * Math.max(0, years - 1);
}

export const COMPETITORS = ['Tally Prime', 'Busy', 'Zoho Books + Inventory', 'Marg', 'Vyapar'];

/** What is not known is said so: "Not checked" means no vendor page we read answered it. */
export const FEATURE_ROWS = [
  {
    label: 'Where it runs',
    ibigdo: 'In the browser or on a phone, hosted by iBigDo. Needs the internet; no server or PC to look after.',
    others: [
      'A PC licence (Windows). Remote access through Tally.NET or hosting, which costs extra.',
      'Desktop software. Cloud not checked.',
      'Zoho runs in the browser.',
      'Desktop, with a cloud edition for a yearly fee.',
      'Phone app and Windows desktop that sync.',
    ],
  },
  {
    label: 'Many people, each with their own screens',
    ibigdo: 'A role for each job (purchase, weighbridge, stores, production, quality, sales, finance). Each person sees only their screens.',
    others: [
      'User security levels and rights by role.',
      'User rights and approvals.',
      'Roles and permissions; detail not checked.',
      'More than one user on higher editions only; detail not checked.',
      'Role-based access claimed.',
    ],
  },
  {
    label: 'Bill of materials and production',
    ibigdo: 'Bills of materials, work orders with wastage, job work challans, from the Professional plan.',
    others: [
      'Multi-level bills of materials, production orders, job work vouchers.',
      'Multi-level bills of materials and production claimed.',
      'Composite items (a product made of parts). No production orders found.',
      'Production module claimed; depth not checked.',
      'Claimed on a marketing page; depth not checked.',
    ],
  },
  {
    label: 'Batches, lots and godowns',
    ibigdo: 'Stock by lot with its cost. Transfers between warehouses from the Standard plan.',
    others: [
      'Batches with expiry, godowns, racks.',
      'Batches, serial numbers, godowns.',
      'Batch tracking from Inventory Professional.',
      'Batch and expiry, multi-store.',
      'Batch and expiry claimed.',
    ],
  },
  {
    label: 'Weighbridge slip and quality check as steps of buying',
    ibigdo: 'A purchase order, goods receipt and vendor bill are one chain, and a bill is matched to what arrived. Weighbridge slips and quality checks are steps in it from the Professional plan.',
    others: ['Not checked', 'Not checked', 'Not checked', 'Not checked', 'Not checked'],
  },
  {
    label: 'What changed, who changed it, and when',
    ibigdo: 'An edit log of every document and master with old and new values. Nobody, an admin included, can edit or delete it.',
    others: [
      'An audit trail feature.',
      'Audit trail claimed.',
      'Not checked',
      'Not checked',
      'Not checked',
    ],
  },
  {
    label: 'e-Invoice and e-Way bill',
    ibigdo: 'e-Way bill from the Standard plan, e-invoice from Professional. Both are prepared and tracked in the app but not yet connected to the government portal: the numbers are sandbox numbers until a live provider is added.',
    others: [
      'Generated live, single or in bulk.',
      'Generated; some on a higher edition.',
      'Not checked',
      'Listed as a feature.',
      'Claimed in the app.',
    ],
    gap: true,
  },
  {
    label: 'Online store for your own customers',
    ibigdo: 'A hosted store page (ibigdo.com/shop/your-name), from the Professional plan: cart, GST by the buyer\'s state, shipping by weight and state, stock checked. Buyers pay by card, UPI or net banking through the business\'s own Razorpay account (the money goes straight to them), or by UPI, bank transfer or cash on delivery. Orders reserve stock and flow into sales; one click makes the GST tax invoice and its receipt, and staff send, deliver, take returns and refund from one screen, with a sales report. Links shared on WhatsApp show a preview card. Not yet: product variants (sizes, colours on one page), coupons, courier booking and live tracking, a store on the business\'s own domain, matching Razorpay payouts to the bank, and no link to an existing website.',
    others: ['Not checked', 'Not checked', 'Not checked', 'Not checked', 'Not checked'],
    gap: true,
  },
  {
    label: 'Payroll',
    ibigdo: 'Not offered.',
    others: ['Offered.', 'Not checked', 'Not checked', 'Not checked', 'Not checked'],
    gap: true,
  },
  {
    label: 'Works without the internet',
    ibigdo: 'No.',
    others: ['Yes, on the PC.', 'Not checked', 'No.', 'Not checked', 'Not checked'],
    gap: true,
  },
];

export const WHEN_NOT_TO_PITCH = [
  'One person keeps the books on one PC and nobody else enters anything. Tally or Vyapar fits and costs less. Say so; they will remember it.',
  'They need payroll in the same software, or must work with the internet down.',
  'They need courier booking and live tracking from the software, product variants (sizes and colours on one page), discount coupons, or a store on their own domain. The online store does not do these yet.',
  'They sell a service rather than goods: meal or tiffin plans, memberships, subscriptions, bookings, catering. The store sells products with stock; recurring plans and delivery rosters are a different product, so pitch the back office (purchase, stock, billing, accounts) and say so plainly.',
  'They must file e-invoices and e-way bills from the software today. Ours are not connected to the government portal yet.',
  'Their accountant insists on Tally and will not look at another tool. Offer the Tally export, but expect the sale to take longer.',
];

export const TALK_TRACK = [
  {
    say: 'On the price tag, yes.',
    detail: 'Then compare what you pay over the years you will use it, and who it works for. The table below does that sum.',
  },
  {
    say: 'A Tally licence is for a PC; we are a service.',
    detail: 'Tally Silver is one user on one PC. Many users, working from anywhere, means Gold, a server or hosting, and backups you look after. Our price includes hosting, backups, updates and support.',
  },
  {
    say: 'Compare like with like.',
    detail: 'Starter is for buying, selling and accounts. A business that makes things and weighs trucks needs Professional, so set Tally Gold, not Silver, beside it.',
  },
  {
    say: 'If they also want to sell online, that is in the plan.',
    detail: 'Professional includes the online store with Razorpay payments on their own account, GST invoices, returns and a sales report, all from the same stock. Ask what they sell, how many orders a day and how they ship; a store that needs variants, coupons or courier booking should hear now that those are not there yet.',
  },
  {
    say: 'Ask how buying and stock work today.',
    detail: 'Who raises the order, who weighs the truck, who checks quality, who books the bill, and how do they know what was received against what was billed? If that answer is a phone call and a notebook, the extra price pays for itself.',
  },
  {
    say: 'If they only need billing, send them to a simpler product.',
    detail: 'Vyapar and Tally are good at that. A customer who feels you were honest brings you the next one.',
  },
];

export const SOURCES = [
  ['Tally Prime prices (reseller guide)', 'https://tallysupport.in/guides/tallyprime-price-list-2026.php'],
  ['Tally Prime for small manufacturers (Tally)', 'https://tallysolutions.com/accounting/tallyprime-small-manufacturers-gst-production-payroll/'],
  ['Busy pricing (Busy)', 'https://busy.in/pricing/'],
  ['Busy features and FAQs (Busy)', 'https://busy.in/busy-features-and-faqs'],
  ['Zoho Books pricing (Zoho)', 'https://www.zoho.com/in/books/pricing/'],
  ['Zoho Inventory pricing (Zoho)', 'https://zstatic.zohostatic.com/in/inventory/pricing'],
  ['Marg ERP pricing (directory)', 'https://www.erpresearch.com/pricing/marg-erp'],
  ['Vyapar pricing (reseller listing)', 'https://www.techjockey.com/detail/vyapar'],
  ['Vyapar business management (Vyapar)', 'https://vyaparapp.in/business-management-software'],
];
